import { useState, useEffect } from "react";
import { CheckCircle, Lock, XCircle, Shield, User } from "lucide-react";
import { supabase } from "./supabase";
import { KO, sendPushToEmployee } from "./helpers";

export function AdminApproval({lang, refreshKey}){
  const [pending,setPending]=useState([]);
  const [terminated,setTerminated]=useState([]);
  const [approved,setApproved]=useState([]);
  const [allProfiles,setAllProfiles]=useState([]);
  const [approveModal,setApproveModal]=useState(null);
  const [empForm,setEmpForm]=useState({position:"",dept:"",join_date:"",vac_adjust:"0",note:""});
  const [tab,setTab]=useState("approval"); // "approval" | "roles"
  const [roleChanging,setRoleChanging]=useState(null);
  const KO=lang==="kr";
  const SP=lang==="es";

  useEffect(()=>{ fetchAll(); },[refreshKey]);
  async function fetchAll(){
    const{data}=await supabase.from("profiles").select("*").order("created_at",{ascending:false});
    const{data:emps}=await supabase.from("employees").select("name,status");
    const inactiveNames=new Set((emps||[]).filter(e=>e.status==="Inactive").map(e=>e.name));
    if(data){
      const notApproved=data.filter(p=>!p.approved&&p.role!=="admin");
      // employees 테이블에서 Inactive(퇴사 처리)로 표시된 사람은 "신규 가입 대기"가 아니라 "퇴사 처리됨"으로 분리
      setPending(notApproved.filter(p=>!inactiveNames.has(p.emp_name)));
      setTerminated(notApproved.filter(p=>inactiveNames.has(p.emp_name)));
      setApproved(data.filter(p=>p.approved&&p.role!=="admin"));
      setAllProfiles(data.filter(p=>p.approved));
    }
  }

  async function changeRole(id, newRole){
    if(!window.confirm(KO
      ? `역할을 ${newRole==="admin"?"관리자":"일반 직원"}로 변경하시겠습니까?`
      : SP
      ? `¿Cambiar el rol a ${newRole==="admin"?"Administrador":"Empleado"}?`
      : `Change role to ${newRole==="admin"?"Admin":"Employee"}?`
    )) return;
    setRoleChanging(id);
    await supabase.from("profiles").update({role:newRole}).eq("id",id);
    await fetchAll();
    setRoleChanging(null);
  }

  function openApproveModal(prof){
    // 오늘 날짜를 MM/DD/YYYY 형식으로
    const today=new Date();
    const mm=String(today.getMonth()+1).padStart(2,"0");
    const dd=String(today.getDate()).padStart(2,"0");
    const yyyy=today.getFullYear();
    setEmpForm({position:"",dept:"",join_date:`${mm}/${dd}/${yyyy}`,vac_adjust:"0",note:""});
    setApproveModal(prof);
  }

  async function approve(){
    if(!approveModal) return;
    const{id,emp_name,birthday,phone}=approveModal;
    // 입사일 변환 MM/DD/YYYY → YYYY-MM-DD
    const parsedJoin=(()=>{
      const p=empForm.join_date.split("/");
      if(p.length===3) return `${p[2]}-${p[0].padStart(2,"0")}-${p[1].padStart(2,"0")}`;
      return new Date().toISOString().slice(0,10);
    })();
    await supabase.from("profiles").update({approved:true}).eq("id",id);
    const{data:existing}=await supabase.from("employees").select("id").eq("name",emp_name).single();
    if(!existing){
      await supabase.from("employees").insert([{
        name:emp_name,
        birthday:birthday||null,
        phone:phone||null,
        join_date:parsedJoin,
        position:empForm.position,
        dept:empForm.dept,
        status:"Active",
        vac_adjust:parseFloat(empForm.vac_adjust)||0,
        note:empForm.note
      }]);
    } else {
      await supabase.from("employees").update({
        birthday:birthday||null,
        phone:phone||null,
        join_date:parsedJoin,
        position:empForm.position,
        dept:empForm.dept,
        vac_adjust:parseFloat(empForm.vac_adjust)||0,
        note:empForm.note
      }).eq("id",existing.id);
    }
    setApproveModal(null);
    fetchAll();
    sendPushToEmployee(
      supabase, emp_name,
      "계정 승인 완료", "관리자가 계정을 승인했습니다. 로그인해서 이용해보세요.",
      "Account Approved", "Your account has been approved by the admin. You can now log in.",
      "/", {title:"Cuenta aprobada", body:"El administrador aprobó tu cuenta. Ya puedes iniciar sesión."}
    );
  }
  async function reject(id){
    if(window.confirm(KO?"이 계정을 삭제하시겠습니까?":SP?"¿Eliminar esta cuenta?":"Delete this account?")){
      await supabase.from("profiles").delete().eq("id",id);
      await fetch("https://spjouqdizmcpehsspfed.supabase.co/functions/v1/delete-user", {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNwam91cWRpem1jcGVoc3NwZmVkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzIzMDkzODYsImV4cCI6MjA4Nzg4NTM4Nn0.7iFv3UaOuBlJgWisoHG0WMmKy4eLdQt82pH2oWp-7OI"},
        body:JSON.stringify({user_id:id})
      });
      fetchAll();
    }
  }
  async function revoke(id){
    if(window.confirm(KO?"승인을 취소하시겠습니까?":SP?"¿Revocar el acceso?":"Revoke access?")){
      await supabase.from("profiles").update({approved:false}).eq("id",id);
      fetchAll();
    }
  }
  async function reinstate(p){
    if(!window.confirm(KO?`${p.emp_name}님을 복직 처리하시겠습니까? 로그인 및 재직 상태가 복구됩니다.`:SP?`¿Reincorporar a ${p.emp_name}? Se restaurarán el acceso y el estado activo.`:`Reinstate ${p.emp_name}? Login access and Active status will be restored.`)) return;
    await supabase.from("profiles").update({approved:true}).eq("id",p.id);
    await supabase.from("employees").update({status:"Active"}).eq("name",p.emp_name);
    fetchAll();
    sendPushToEmployee(
      supabase, p.emp_name,
      "계정 복구 완료", "관리자가 계정을 복구했습니다. 로그인해서 이용해보세요.",
      "Account Reinstated", "Your account has been reinstated by the admin. You can now log in.",
      "/", {title:"Cuenta restablecida", body:"El administrador restableció tu cuenta. Ya puedes iniciar sesión."}
    );
  }

  return(
    <>
      {/* 승인 모달 */}
      {approveModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:"var(--sf)",borderRadius:12,padding:24,width:"100%",maxWidth:400,maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{fontWeight:700,fontSize:16,marginBottom:4}}>{KO?"직원 정보 입력":SP?"Datos del empleado":"Employee Info"}</div>
            <div style={{fontSize:13,color:"var(--tx3)",marginBottom:16}}>{approveModal.emp_name}</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div><label style={{fontSize:12,color:"var(--tx2)",display:"block",marginBottom:4}}>{KO?"직책":SP?"Puesto":"Position"}</label>
                <input value={empForm.position} onChange={e=>setEmpForm(p=>({...p,position:e.target.value}))}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13,boxSizing:"border-box"}}/></div>
              <div><label style={{fontSize:12,color:"var(--tx2)",display:"block",marginBottom:4}}>{KO?"부서":SP?"Departamento":"Department"}</label>
                <input value={empForm.dept} onChange={e=>setEmpForm(p=>({...p,dept:e.target.value}))}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13,boxSizing:"border-box"}}/></div>
              <div><label style={{fontSize:12,color:"var(--tx2)",display:"block",marginBottom:4}}>{KO?"입사일":SP?"Fecha de ingreso":"Join Date"}</label>
                <input value={empForm.join_date} onChange={e=>setEmpForm(p=>({...p,join_date:e.target.value}))} placeholder="MM/DD/YYYY"
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13,boxSizing:"border-box"}}/></div>
              <div><label style={{fontSize:12,color:"var(--tx2)",display:"block",marginBottom:4}}>{KO?"연차 조정 (일)":SP?"Ajuste de vacaciones (días)":"Vac Adjust (days)"}</label>
                <input type="number" step="0.5" value={empForm.vac_adjust} onChange={e=>setEmpForm(p=>({...p,vac_adjust:e.target.value}))}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13,boxSizing:"border-box"}}/></div>
              <div><label style={{fontSize:12,color:"var(--tx2)",display:"block",marginBottom:4}}>{KO?"메모":SP?"Nota":"Note"}</label>
                <input value={empForm.note} onChange={e=>setEmpForm(p=>({...p,note:e.target.value}))}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13,boxSizing:"border-box"}}/></div>
            </div>
            <div style={{display:"flex",gap:8,marginTop:16}}>
              <button className="btn bp" style={{flex:1}} onClick={approve}><CheckCircle size={14} style={{marginRight:4}}/>{KO?"승인":SP?"Aprobar":"Approve"}</button>
              <button className="btn bsec" style={{flex:1}} onClick={()=>setApproveModal(null)}>{KO?"취소":SP?"Cancelar":"Cancel"}</button>
            </div>
          </div>
        </div>
      )}
      <div className="ph">
        <div className="pt"><Lock size={20}/> {KO?"직원 계정 관리":SP?"Gestión de cuentas":"Account Management"}</div>
        <div className="ps">{KO?"직원 계정 승인 및 역할을 관리합니다.":SP?"Administra las aprobaciones de cuentas y los roles de los empleados.":"Manage employee account approvals and roles."}</div>
      </div>

      <div className="tabs" style={{marginBottom:20}}>
        <button className={`tab ${tab==="approval"?"active":""}`} onClick={()=>setTab("approval")}>
          {KO?"직원 승인":SP?"Aprobaciones":"Approvals"}{pending.length>0&&<span style={{marginLeft:6,background:"var(--re)",color:"#fff",borderRadius:"50%",fontSize:10,fontWeight:700,padding:"1px 5px"}}>{pending.length}</span>}
        </button>
        <button className={`tab ${tab==="roles"?"active":""}`} onClick={()=>setTab("roles")}>
          {KO?"역할 관리":SP?"Gestión de roles":"Role Management"}
        </button>
      </div>

      {tab==="approval" && <>
        <div className="ct" style={{marginBottom:8}}>{KO?"대기 중":SP?"Pendientes":"Pending"} ({pending.length})</div>
        {pending.length===0
          ?<div className="card"><div className="empty">{KO?"대기 중인 신청 없음":SP?"Sin solicitudes pendientes":"No pending requests"}</div></div>
          :pending.map(p=>(
            <div className="card" key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
              <div>
                <div style={{fontWeight:700,fontSize:15}}>{p.emp_name}</div>
                <div style={{fontSize:12,color:"var(--tx3)"}}>{new Date(p.created_at).toLocaleDateString(SP?"es-US":undefined)}</div>
              </div>
              <div className="approval-btns" style={{display:"flex",gap:8}}>
                <button className="btn bp" onClick={()=>openApproveModal(p)}><CheckCircle size={14} style={{marginRight:4}}/>{KO?"승인":SP?"Aprobar":"Approve"}</button>
                <button className="btn bd2" onClick={()=>reject(p.id)}><XCircle size={14} style={{marginRight:4}}/>{KO?"거절":SP?"Rechazar":"Reject"}</button>
              </div>
            </div>
          ))
        }
        <div className="ct" style={{margin:"16px 0 8px"}}>{KO?"승인된 직원":SP?"Aprobados":"Approved"} ({approved.length})</div>
        {approved.map(p=>(
          <div className="card" key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{fontWeight:700,fontSize:15}}>{p.emp_name}</div>
              <div style={{fontSize:12,color:"var(--tx3)"}}>{p.id.slice(0,8)}...</div>
            </div>
            <button className="btn bsec" style={{fontSize:12}} onClick={()=>revoke(p.id)}>{KO?"승인 취소":SP?"Revocar":"Revoke"}</button>
          </div>
        ))}
        {terminated.length>0&&<>
          <div className="ct" style={{margin:"16px 0 8px",color:"var(--tx3)"}}>{KO?"퇴사 처리됨":SP?"Dados de baja":"Terminated"} ({terminated.length})</div>
          {terminated.map(p=>(
            <div className="card" key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,opacity:.7}}>
              <div>
                <div style={{fontWeight:700,fontSize:15}}>{p.emp_name}</div>
                <div style={{fontSize:12,color:"var(--tx3)"}}>{KO?"로그인 차단됨":SP?"Acceso revocado":"Access revoked"}</div>
              </div>
              <button className="btn bsec" style={{fontSize:12}} onClick={()=>reinstate(p)}>{KO?"복직 처리":SP?"Reincorporar":"Reinstate"}</button>
            </div>
          ))}
        </>}
      </>}

      {tab==="roles" && <>
        <div style={{fontSize:13,color:"var(--tx3)",marginBottom:16}}>
          {KO?"승인된 직원의 역할을 변경합니다. 관리자는 모든 기능에 접근할 수 있습니다.":SP?"Cambia los roles de los empleados aprobados. Los administradores tienen acceso a todas las funciones.":"Change roles for approved employees. Admins have full access to all features."}
        </div>
        {allProfiles.length===0
          ?<div className="card"><div className="empty">{KO?"승인된 직원 없음":SP?"Sin empleados aprobados":"No approved employees"}</div></div>
          :allProfiles.map(p=>(
            <div className="card" key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:p.role==="admin"?"rgba(74,122,181,.15)":"var(--sf2)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {p.role==="admin"?<Shield size={16} color="var(--ac)"/>:<User size={16} color="var(--tx3)"/>}
                </div>
                <div>
                  <div style={{fontWeight:700,fontSize:14}}>{p.emp_name}</div>
                  <div style={{fontSize:11,color:p.role==="admin"?"var(--ac)":"var(--tx3)",fontWeight:600}}>
                    {p.role==="admin"?(KO?"관리자":SP?"Administrador":"Admin"):(KO?"일반 직원":SP?"Empleado":"Employee")}
                  </div>
                </div>
              </div>
              <button
                onClick={()=>changeRole(p.id, p.role==="admin"?"employee":"admin")}
                disabled={roleChanging===p.id}
                style={{fontSize:12,padding:"6px 14px",borderRadius:8,border:"none",cursor:"pointer",fontWeight:600,
                  background:p.role==="admin"?"rgba(220,38,38,.08)":"rgba(74,122,181,.1)",
                  color:p.role==="admin"?"var(--re)":"var(--ac)"}}>
                {roleChanging===p.id?"...":(p.role==="admin"?(KO?"일반 직원으로":SP?"Cambiar a empleado":"Demote"):(KO?"관리자로 승격":SP?"Promover a administrador":"Promote"))}
              </button>
            </div>
          ))
        }
      </>}
    </>
  );
}
